import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { InboxRow, EmptyInbox } from "../components/inbox/InboxBits";
import InboxDetail from "../components/inbox/InboxDetail";
import MailboxSettingsDialog from "../components/inbox/MailboxSettingsDialog";
import PasteEmailDialog from "../components/inbox/PasteEmailDialog";
import { InboxActions, InboxStats, ViewChips } from "../components/inbox/InboxHeader";
import { Envelope, MagnifyingGlass, WarningCircle } from "@phosphor-icons/react";

function useIsDesktop() {
  const [desktop, setDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setDesktop(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return desktop;
}

export default function EmailInboxPage() {
  const isDesktop = useIsDesktop();
  const [params, setParams] = useSearchParams();
  const deepLink = params.get("item") || "";
  const [view, setView] = useState(deepLink ? "all" : "new");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);
  const [selectedId, setSelectedId] = useState(deepLink);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pulling, setPulling] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: c }] = await Promise.all([API.get("/inbox", { params: { view, search, limit: 100 } }), API.get("/inbox/counts")]);
      setItems(data.items); setTotal(data.total); setCounts(c);
    } catch { toast.error("Couldn't load the inbox"); }
    finally { setLoading(false); }
  }, [view, search]);

  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    API.get(`/inbox/${selectedId}`).then(({ data }) => setSelected(data)).catch(() => setSelected(null));
  }, [selectedId, items]);

  useEffect(() => {
    if (deepLink && deepLink !== selectedId) { setView("all"); setSelectedId(deepLink); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const pick = (id) => { setSelectedId(id); if (deepLink) setParams(p => { p.delete("item"); return p; }, { replace: true }); };
  const changeView = (v) => { setView(v); pick(""); };

  const pullNow = async () => {
    setPulling(true);
    try {
      const { data } = await API.post("/inbox/poll");
      toast.success(data.imported ? `Pulled ${data.imported} new email${data.imported === 1 ? "" : "s"}` : "Mailbox checked — nothing new");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPulling(false); }
  };

  const connected = counts?.connected;
  const detail = <InboxDetail item={selected} smtpReady={!!counts?.smtp_ready} onChanged={() => { load(); }} />;

  return (
    <div className="p-4 sm:p-6 h-[calc(100vh-4rem)] flex flex-col gap-4" data-testid="email-inbox-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-[Chivo] font-bold tracking-tight flex items-center gap-2"><Envelope size={26} weight="duotone" className="text-primary" /> Email inbox</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-4xl" data-testid="inbox-explainer">
          Emails sent to your maintenance mailbox land here, matched to the client. Turn them into job requests in one tap — or let known clients' emails become requests automatically.
        </p>
      </div>

      <InboxActions counts={counts} pulling={pulling} onPaste={() => setPasteOpen(true)} onPull={pullNow} onSettings={() => setSettingsOpen(true)} onChanged={load} />

      <InboxStats counts={counts} view={view} onView={changeView} />

      {counts && !counts.smtp_ready && (
        <div className="rounded-md border border-red-300/70 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/20 px-4 py-2.5 text-sm text-red-900 dark:text-red-200 flex items-center gap-2" data-testid="inbox-smtp-warning">
          <WarningCircle size={16} weight="fill" className="shrink-0 text-red-600" />
          <span>Replying from the inbox is off until the <strong>Sender replies (SMTP)</strong> mailbox is set up in <button type="button" className="underline underline-offset-2 font-medium" onClick={() => setSettingsOpen(true)} data-testid="inbox-smtp-warning-settings">Mailbox settings</button>.</span>
        </div>
      )}
      {counts?.emergency ? (
        <div className="rounded-md border-2 border-red-500 bg-red-50 dark:bg-red-950/30 px-4 py-2.5 text-sm font-semibold text-red-800 dark:text-red-200 flex items-center justify-between gap-2" data-testid="inbox-emergency-strip">
          <span>{counts.emergency} emergency email{counts.emergency === 1 ? "" : "s"} not raised yet — review and raise now.</span>
          <Button size="sm" variant="outline" className="border-red-400 text-red-700 hover:bg-red-100" onClick={() => changeView("urgent")} data-testid="inbox-emergency-strip-btn">Show urgent</Button>
        </div>
      ) : null}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 items-stretch">
        <div className="flex flex-col gap-3 min-w-0 min-h-0">
          <ViewChips view={view} counts={counts} onView={changeView} />
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search sender, subject, text…" className="pl-8 h-8 text-xs" data-testid="inbox-search" />
          </div>
          <div className={`rounded-md border ${items.length ? "border-border bg-card" : "border-dashed border-border/80"} flex flex-col flex-1 min-h-[140px]`}>
            <div className="flex-1 min-h-0 overflow-y-auto" data-testid="inbox-list">
              {loading ? <p className="text-xs text-muted-foreground p-4">Loading…</p>
                : items.length === 0 ? <EmptyInbox view={view} connected={connected} />
                  : items.map(it => <InboxRow key={it.id} item={it} active={it.id === selectedId} onClick={() => pick(it.id)} />)}
            </div>
            <div className="px-3 py-1.5 border-t border-border/60 text-[10px] text-muted-foreground tabular-nums" data-testid="inbox-list-footer">
              {items.length} of {total}{counts?.urgent ? ` · ${counts.urgent} urgent waiting` : ""}{counts?.overdue ? <span className="text-red-600"> · {counts.overdue} overdue reply</span> : null}{counts?.emergency ? <span className="text-red-600 font-semibold" data-testid="inbox-emergency-count"> · {counts.emergency} emergency not raised</span> : null}
            </div>
          </div>
        </div>

        {isDesktop && (
          <div className={`rounded-md border min-h-0 h-full overflow-hidden ${selected ? "border-border bg-card" : "border-dashed border-border/80 flex items-center justify-center"}`} data-testid="inbox-detail-pane">
            {selected ? detail : <p className="text-sm text-muted-foreground text-center px-6" data-testid="inbox-detail-placeholder">Select an email to review, convert it to a job request, or add it to an existing one.</p>}
          </div>
        )}
        {!isDesktop && selected && (
          <div className="fixed inset-0 z-40 bg-background flex flex-col" data-testid="inbox-detail-mobile">
            <div className="p-2 border-b border-border"><Button variant="ghost" size="sm" onClick={() => setSelectedId("")}>← Back to list</Button></div>
            <div className="flex-1 min-h-0">{detail}</div>
          </div>
        )}
      </div>

      <MailboxSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} onSaved={load} />
      <PasteEmailDialog open={pasteOpen} onOpenChange={setPasteOpen} onImported={(d) => { load(); setView(d.status === "new" ? "new" : d.status === "ignored" || d.status === "auto_ignored" ? "ignored" : d.status === "added" ? "threaded" : "converted"); setSelectedId(d.id); }} />
    </div>
  );
}
